import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import {
    withRouter,
    withIdParams,
} from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    actionMenuStatus,
    FormBox,
    FormFrame,
    PageSpinner,
    renderMarkdown,
} from "@impero-com/ui";
import { Spin } from "antd";
import axios, { CancelTokenSource } from "axios";
import { isEqual } from "lodash-es";
import { NavigateFunction } from "react-router-dom";

import type { UserProfile } from "api/generated/model";
import { UpdatableRiskDirectory } from "api/generated/model";
import {
    getRisks,
    getRisksRiskDirectoryId,
    deleteRisksRiskDirectoryId,
    patchRisksRiskDirectoryId,
} from "api/generated/risks/risks";
import { AccessRights, AccessRightsValue } from "components/accessRights";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import IconDelete from "img/delete.svg";
import * as R from "org/risks/types";
import {
    convertAdminEditViewAccessRightsToUserAndRights,
    convertUsersAndRightsToAdminEditViewAccessRights,
} from "utils/accessRights/converters";
import {
    RiskDirectoryAccessRightsInstance,
    RiskDirectoryAccessRights,
} from "utils/accessRights/models";
import { FormCreate, FormRootComponentProps } from "utils/form";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { RISK_MODULE } from "../url";

function convAccessRightsToEditor(
    riskDirectory: R.RiskDirectoryView
): AccessRightsValue<RiskDirectoryAccessRights> {
    const {
        content: {
            resource: { responsibleId, accessRights },
        },
        linkedResources: { users },
    } = riskDirectory;
    const usersAndRights = convertAdminEditViewAccessRightsToUserAndRights(
        users,
        accessRights
    );
    return {
        responsible: users.find((user) => user.id === responsibleId),
        usersAndRights,
    };
}

function convAccessRightsToBackend(
    accessRights: AccessRightsValue<RiskDirectoryAccessRights>
): UpdatableRiskDirectory {
    const { responsible, usersAndRights } = accessRights;
    return {
        responsibleId: responsible?.id,
        accessRights:
            convertUsersAndRightsToAdminEditViewAccessRights(usersAndRights),
    };
}

interface AppBarProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
}

interface RiskDirectoryEditorFormProps
    extends FormRootComponentProps<object>, AppBarProps {
    navigate: NavigateFunction;
    id: string;
}

type RiskDirectoryEditorStatus =
    | { type: "DEFAULT" }
    | { type: "LOADING" }
    | { type: "ERROR"; code?: number }
    | {
          type: "LOADED" | "UPDATING";
          riskDirectory: R.RiskDirectoryView;
      };

interface RiskDirectoryEditorFormState {
    status: RiskDirectoryEditorStatus;
    deleteDialogIsOpen: boolean;
}

class RiskDirectoryEditorForm extends React.PureComponent<
    RiskDirectoryEditorFormProps,
    RiskDirectoryEditorFormState
> {
    constructor(props: RiskDirectoryEditorFormProps) {
        super(props);
        this.state = {
            status: {
                type: "DEFAULT",
            },
            deleteDialogIsOpen: false,
        };
    }

    cancelTokenSource: CancelTokenSource[] = [];

    componentDidMount = () => {
        const { id } = this.props;
        this.load(id);
    };

    componentDidUpdate = (
        prevProps: Readonly<RiskDirectoryEditorFormProps>
    ) => {
        const { id } = this.props;
        if (prevProps.id !== id) {
            this.load(id);
        }
    };

    componentWillUnmount() {
        this.cancelAllUpdate();
    }

    cancelAllUpdate() {
        this.cancelTokenSource.forEach((cts) => cts.cancel());
        this.cancelTokenSource = [];
    }

    load = (id: string) => {
        const { ajaxManager } = this.props;
        ajaxManager.ajax<R.RiskDirectoryView, RiskDirectoryEditorStatus>(
            () =>
                getRisksRiskDirectoryId(Number(id)).then((response) => ({
                    ...response,
                    data: response.data,
                })),
            {
                component: this,
                initialStatusValue: "DEFAULT",
                getErrorStatus: (code) => ({ type: "ERROR", code }),
                inFlightStatus: { type: "LOADING" },
                getSuccessStatus: ([riskDirectory]) => ({
                    type: "LOADED",
                    riskDirectory,
                }),
            }
        );
    };

    delete = () => {
        const { status } = this.state;
        if (status.type !== "LOADED") {
            throw new Error(
                "Couldn't delete risk directory: No risk directory loaded"
            );
        }

        const {
            riskDirectory: {
                content: {
                    resource: { id },
                },
            },
        } = status;
        const { ajaxManager, navigate } = this.props;

        ajaxManager
            .ajax<void, RiskDirectoryEditorStatus>(
                () => deleteRisksRiskDirectoryId(id),
                {
                    component: this,
                    initialStatusValue: "LOADED",
                    getErrorStatus: (code) => ({ type: "ERROR", code }),
                    inFlightStatus: { type: "LOADING" },
                    getSuccessStatus: () => ({
                        type: "DEFAULT",
                    }),
                }
            )
            .then(() => navigate(RISK_MODULE));
    };

    deleteRiskDirectoryDialog = () => {
        const { deleteDialogIsOpen, status } = this.state;
        if (!deleteDialogIsOpen || status.type !== "LOADED") {
            return;
        }

        const { riskDirectory } = status;

        return (
            <DeleteWithConfirmationDialog
                title={t("RiskDirectory_Delete_Title")}
                onConfirm={this.delete}
                onCancel={() => {
                    this.setState({ deleteDialogIsOpen: false });
                }}
            >
                {renderMarkdown(
                    t("RiskDirectory_Delete_Body", {
                        name: riskDirectory.content.resource.title,
                    })
                )}
            </DeleteWithConfirmationDialog>
        );
    };

    render = () => {
        const { status } = this.state;

        switch (status.type) {
            case "LOADED":
            case "UPDATING":
                return this.withApplicationBar(
                    status.riskDirectory,
                    <>
                        {this.deleteRiskDirectoryDialog()}
                        <Spin spinning={status.type !== "LOADED"}>
                            <Form>{this.renderBody(status.riskDirectory)}</Form>
                        </Spin>
                    </>
                );
            case "ERROR":
                switch (status.code) {
                    case 403:
                        return (
                            <OrgApplicationBar
                                title={{
                                    title: "",
                                    breadCrumbs: [
                                        {
                                            label: t(
                                                "RiskDirectory_RiskManager"
                                            ),
                                            url: RISK_MODULE,
                                        },
                                    ],
                                }}
                            >
                                <NoAccessCard />
                            </OrgApplicationBar>
                        );
                    default:
                        return <LoggedInHTTPError code={status.code} />;
                }
            case "LOADING":
            case "DEFAULT":
                return <PageSpinner />;
            default:
                assertNever(status);
        }
    };

    renderBody = (riskDirectory: R.RiskDirectoryView) => {
        const { title, description } = riskDirectory.content.resource;
        const accessRights = convAccessRightsToEditor(riskDirectory);
        const editable = riskDirectory.content.actions.edit;
        const accessRightsEditable = riskDirectory.content.actions.admin;
        const resourceAccessRights = RiskDirectoryAccessRightsInstance;

        return (
            <FormFrame>
                <FormBox>
                    {editable ? (
                        <StringInput
                            type="EDIT"
                            id="title"
                            label={t("RiskDirectory_Label_Title")}
                            value={title}
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    kind: "async",
                                    asyncValidator: this.checkTitleAvailability,
                                },
                            ]}
                            onChange={(title) =>
                                this.updateRiskDirectory({ title })
                            }
                        />
                    ) : (
                        <StringInput
                            type="READ_ONLY"
                            label={t("RiskDirectory_Label_Title")}
                            value={title}
                        />
                    )}
                </FormBox>
                <FormBox>
                    {editable ? (
                        <TextAreaInput
                            type="EDIT"
                            id="description"
                            label={t("RiskDirectory_Label_Description")}
                            value={description}
                            onChange={(description) =>
                                this.updateRiskDirectory({ description })
                            }
                        />
                    ) : (
                        <TextAreaInput
                            type="READ_ONLY"
                            label={t("RiskDirectory_Label_Description")}
                            value={description}
                        />
                    )}
                </FormBox>
                <FormBox>
                    {accessRightsEditable ? (
                        <AccessRights
                            type="EDIT"
                            id="accessRights"
                            value={accessRights}
                            onChange={(accessRights) =>
                                this.updateRiskDirectory(
                                    convAccessRightsToBackend(accessRights)
                                )
                            }
                            resourceAccessRights={resourceAccessRights}
                        />
                    ) : (
                        <AccessRights
                            type="READ_ONLY"
                            value={accessRights}
                            resourceAccessRights={resourceAccessRights}
                        />
                    )}
                </FormBox>
            </FormFrame>
        );
    };

    withApplicationBar = (
        riskDirectory: R.RiskDirectoryView,
        children: React.ReactNode
    ) => {
        return (
            <OrgApplicationBar
                title={{
                    title: riskDirectory.content.resource.title,
                    breadCrumbs: [
                        {
                            label: t("RiskDirectory_RiskManager"),
                            url: RISK_MODULE,
                        },
                    ],
                }}
                actions={{
                    id: "riskDirectory_actions",
                    actions: [
                        {
                            id: "riskDirectory_delete_action",
                            label: t("RiskDirectory_Action_Delete"),
                            icon: { svg: IconDelete },
                            status: actionMenuStatus(
                                riskDirectory.content.actions.delete,
                                t("RiskDirectory_Action_Delete_Forbidden")
                            ),
                            onClick: () => {
                                this.setState({ deleteDialogIsOpen: true });
                            },
                        },
                    ],
                }}
            >
                {children}
            </OrgApplicationBar>
        );
    };

    checkTitleAvailability = (title: string | undefined): Promise<void> => {
        const { status } = this.state;

        if (title === undefined) {
            return Promise.resolve(); // Should be ruled-out
        }

        if (
            status.type === "LOADED" &&
            status.riskDirectory.content.resource.title === title
        ) {
            return Promise.resolve(); // title hasn't changed, so no need to check
        }

        return getRisks({ all_directories: true, title }).then((response) => {
            const matchedTitles = response.data;
            if (matchedTitles.content.length > 0) {
                return Promise.reject(
                    new Error(t("RiskDirectory_Creator_Error_TitleExists"))
                );
            } else {
                return Promise.resolve();
            }
        });
    };

    updateRiskDirectory(
        value: UpdatableRiskDirectory
    ): Promise<R.RiskDirectoryView> {
        const { status } = this.state;
        let riskDirectory: R.RiskDirectoryView;
        switch (status.type) {
            case "UPDATING":
            case "DEFAULT":
            case "LOADING":
            case "ERROR":
                throw new Error("Invalid state");
            case "LOADED":
                riskDirectory = status.riskDirectory;
                break;
            default:
                return assertNever(status);
        }

        const newRiskDirectory: R.RiskDirectoryView = {
            ...riskDirectory,
            ...value,
        };

        if (isEqual(riskDirectory, newRiskDirectory)) {
            return Promise.resolve(newRiskDirectory);
        }

        const { id } = riskDirectory.content.resource;
        const { ajaxManager, orgProfileCookie } = this.props;
        const source = axios.CancelToken.source();
        this.cancelTokenSource.push(source);

        const curUserAccessRights =
            orgProfileCookie &&
            convAccessRightsToEditor(riskDirectory).usersAndRights.filter(
                (userAndRight) =>
                    userAndRight.user.id === orgProfileCookie.userId
            );

        return ajaxManager
            .ajax<R.RiskDirectoryView, RiskDirectoryEditorStatus>(
                () =>
                    patchRisksRiskDirectoryId(id, value, {
                        cancelToken: source.token,
                    }).then((response) => ({
                        ...response,
                        data: response.data,
                    })),
                {
                    component: this,
                    initialStatusValue: "LOADED",
                    inFlightStatus: { type: "UPDATING", riskDirectory },
                    getSuccessStatus: ([riskDirectory]) => ({
                        type: "LOADED",
                        riskDirectory,
                    }),
                    getErrorStatus: () => status,
                }
            )
            .then((riskDirectory) => {
                // If the user changed their own access rights, reload the page
                const updatedCurUserAccessRights =
                    orgProfileCookie &&
                    convAccessRightsToEditor(
                        riskDirectory
                    ).usersAndRights.filter(
                        (userAndRight) =>
                            userAndRight.user.id === orgProfileCookie.userId
                    );
                if (!isEqual(updatedCurUserAccessRights, curUserAccessRights)) {
                    location.reload();
                }
                return riskDirectory;
            });
    }
}

export const RiskDirectoryEditor = withRouter(
    withIdParams(
        FormCreate<object, RiskDirectoryEditorFormProps>()(
            withI18n<RiskDirectoryEditorFormProps>(RiskDirectoryEditorForm)
        )
    )
);
